import BaseSwiperStore from '@src/components/swiper/base-swiper/store';
import { useThrottleFn } from 'ahooks';
import { useEffect } from 'react';
import { noop } from '@src/utils/common-util';
import { pageSwiperEffect } from '@src/components/swiper/page-swiper/config';
import { computed, observable } from 'mobx';

class pageSwiperStore {
  /**
   * swiperOptions 默认的属性值
   * @type {{simulateTouch: boolean, observeParents: boolean, releaseOnEdges: boolean, effect: string, mousewheel: boolean, fadeEffect: {}, slidesPerView: number, observe: boolean, direction: string}}
   */
  swiperOptions = {
    direction: 'vertical',
    slidesPerView: 1,
    observe: true,
    observeParents: true,
    mousewheel: false,
    releaseOnEdges: true,
    simulateTouch: false,
    on: {
      slideChangeTransitionStart: () => {
        this.finalPageTransitionEnd = false;
      },
      slideChangeTransitionEnd: () => {
        this.finalPageTransitionEnd = this.baseSwiperStore.activeIndex >= this.baseSwiperStore.size - 1;
      }
    }
  };

  onPageSwiperChange = noop;
  // 最后一页滚动到底部时的回调
  onLastSlideScrollToBottom = noop;

  baseSwiperStore = new BaseSwiperStore({
    swiperOptions: this.swiperOptions
  });

  @observable onChangeFooterState = noop;
  // 是否允许页面上有弹窗开启的时候滚动
  allowScrollWhileModalOpen = false;
  // 是否可以切换
  allowSwitch = true;
  // 最后一页滑动结束
  finalPageTransitionEnd = false;

  constructor({ effectMode = pageSwiperEffect.FADE, ...rest } = {}) {
    Object.assign(this, rest);
    this.baseSwiperStore.swiperOptions = { ...this.baseSwiperStore.swiperOptions, ...effectMode };
  }

  @computed
  get activeIndex() {
    return this.baseSwiperStore.activeIndex;
  }
  /**
   * 获取当前 slide 的可滚动元素
   * @returns {Element|null}
   */
  getCurrentSlideScrollableElement = () => {
    const swiper = this.baseSwiperStore.swiperRef.current?.swiper;
    if (!swiper) return null;
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return null;

    // 检查 slide 本身或其第一个子元素是否可滚动
    const checkScrollable = el => el && el.scrollHeight > el.clientHeight;

    if (checkScrollable(activeSlide)) return activeSlide;

    // 检查第一层子元素
    const firstChild = activeSlide.firstElementChild;
    if (checkScrollable(firstChild)) return firstChild;

    return null;
  };

  /**
   * 检查是否应该触发 swiper 切换
   * @param {boolean} isScrollUp - 是否向上滚动
   * @returns {boolean} - 是否允许切换
   */
  shouldTriggerSwiperSwitch = isScrollUp => {
    const scrollableEl = this.getCurrentSlideScrollableElement();
    if (!scrollableEl) return true; // 没有可滚动元素，直接切换

    const { scrollTop, scrollHeight, clientHeight } = scrollableEl;
    const tolerance = 2; // 容差值，处理小数精度问题

    if (isScrollUp) {
      // 向上滚动：只有滚到顶部才切换上一页
      return scrollTop <= tolerance;
    } else {
      // 向下滚动：只有滚到底部才切换下一页
      return scrollTop + clientHeight >= scrollHeight - tolerance;
    }
  };

  /**
   * 全屏滚动鼠标监听
   * 因为使用swiper自带的鼠标上线滚动会出现页面闪动的问题
   * 因此通过监听鼠标滚动事件来实现全屏滚动
   * @param ev
   */
  handleScroll = ev => {
    let isScrollUp;
    if (ev.wheelDelta) {
      isScrollUp = ev.wheelDelta > 0;
    } else {
      isScrollUp = ev.detail < 0;
    }

    // 检查内部滚动状态，未滚到边界时不触发 swiper 切换
    if (!this.shouldTriggerSwiperSwitch(isScrollUp)) {
      return;
    }

    const currentActiveIndex = this.baseSwiperStore.activeIndex;
    const nextActiveIndex = currentActiveIndex + (isScrollUp ? -1 : 1);
    /**
     * 禁止滚动到 -1 屏
     */
    if (nextActiveIndex < 0) {
      return;
    }

    /**
     * 获取总屏数
     */
    const totalSwiperSlides = this.baseSwiperStore.swiperRef.current.swiper.slides.length;

    /**
     * 其他溢出场景禁止
     */
    if (nextActiveIndex > totalSwiperSlides - 1) {
      return;
    }

    /**
     * 正常场景允许滚动
     */
    this.baseSwiperStore.onChange(nextActiveIndex);
  };

  /**
   * 是否有弹窗打开
   * @returns {boolean}
   */
  hasModalOpen = () => {
    return document?.querySelectorAll('[class*=openModal][data-open=true]').length || document?.querySelectorAll('[class*=age-modal]').length;
  };

  /**
   * 检查最后一页是否滚动到底部
   */
  checkLastSlideScrollPosition = () => {
    const swiper = this.baseSwiperStore.swiperRef.current?.swiper;
    if (!swiper) return;

    const isLastSlide = swiper.activeIndex >= swiper.slides.length - 1;
    if (!isLastSlide) return;

    const scrollableEl = this.getCurrentSlideScrollableElement();
    if (!scrollableEl) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollableEl;
    const tolerance = 50; // 距离底部50px以内视为到底部
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - tolerance;

    this.onLastSlideScrollToBottom(isAtBottom);
  };

  use() {
    const { run: onScroll } = useThrottleFn(
      (...args) => {
        if (!this.allowSwitch) {
          return;
        }
        if (!this.allowScrollWhileModalOpen && this.hasModalOpen()) {
          return;
        }
        this.handleScroll(...args);
      },
      { wait: 1200, leading: true, trailing: false }
    );

    this.lastScrollTime = 0;
    useEffect(
      () => {
        const handleWheel = e => {
          const now = Date.now();
          const timeDiff = now - this.lastScrollTime;

          // 时间间隔小于50ms视为惯性滚动，直接阻止
          if (timeDiff <= 50) {
            return;
          }

          // 普通滚动正常处理
          this.lastScrollTime = now;
          onScroll(e);
        };

        document.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
          document.removeEventListener('wheel', handleWheel);
        };
      },
      [onScroll]
    );

    // 监听最后一页的滚动位置
    useEffect(() => {
      const handleScroll = () => {
        this.checkLastSlideScrollPosition();
      };

      // 监听所有 slide 的滚动事件
      const swiper = this.baseSwiperStore.swiperRef.current?.swiper;
      if (swiper && swiper.slides) {
        const slides = Array.from(swiper.slides);
        slides.forEach(slide => {
          slide.addEventListener('scroll', handleScroll);
        });

        return () => {
          slides.forEach(slide => {
            slide.removeEventListener('scroll', handleScroll);
          });
        };
      }
    }, []);

    useEffect(
      () => {
        this.onPageSwiperChange(this.baseSwiperStore.activeIndex, this.baseSwiperStore.swiperRef.current.swiper.slides.length);
        // 切换页面时也检查滚动位置
        this.checkLastSlideScrollPosition();
      },
      [this.baseSwiperStore.activeIndex]
    );
  }
}

export default pageSwiperStore;
